import { Box, Button, Typography, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  ChevronLeft as BackIcon,
  ChevronRight as NextIcon,
  ShieldOutlined as ShieldIcon,
} from "@mui/icons-material";
import { colorsFoundation } from "../../../theme/index.js";
import { activeBrand } from "../../../theme/brands/index.js";

/**
 * PhaseRail — the Schmidt two-phase claim wizard shell: a phase banner (light for
 * the customer phase, dark navy for the broker phase), a left rail split into two
 * numbered groups ("Ihre Angaben" / "Bearbeitung Makler"), a white content card,
 * a footer nav below it, and a handoff interstitial between the phases. Styled to
 * match the Schmidt click dummy.
 *
 * The end customer walks the customer-phase steps and hands the claim off to the
 * broker (status → awaiting_broker_completion, customer fields frozen server-side);
 * the broker later resumes on the broker-phase steps and forwards to the insurer.
 * Steps are tagged with `phase` by buildClaimWizardSteps.
 */
// Dummy palette (PhaseRail is the schmidt-only wizard shell).
const ACCENT = "#4A78BC";
const NAVY = "#1B2C45";
// Match the app content background so the wizard blends seamlessly into the page
// (no visible seam above/below the shell).
const PAGE_BG = colorsFoundation.gray[50];
const CARD_BORDER = "rgba(0,0,0,0.06)";
const RAIL_BORDER = "#e3e8ef";
const RAIL_LINE = "#dfe5ee";
const CHIP_BG = "#EEF3FB";
const MUTED = "#8a97a8";
const TITLE = "#111827";
const INTRO = "#6b7280";
const HAIRLINE = colorsFoundation.gray[200];

const BROKER_STATUSES = [
  "awaiting_broker_completion",
  "pre_check",
  "processing",
  "correction_insurer",
  "processed",
  "released",
  "settled",
  "partial_settled",
];

export default function PhaseRail(props) {
  const {
    item,
    errors = null,
    renderFields,
    actionContext,
    values,
    isSubFormOpen = false,
    claimStatus = null,
    isBroker = false,
  } = props;

  const steps = useMemo(
    () =>
      (item.steps || []).filter(
        (s) => typeof s.when !== "function" || s.when(values)
      ),
    [item.steps, values]
  );

  const customerSteps = steps.filter((s) => (s.phase || "customer") !== "broker");
  const brokerSteps = steps.filter((s) => s.phase === "broker");
  const hasBroker = brokerSteps.length > 0;

  // Linear flow: customer steps → handoff interstitial → broker steps.
  const flow = [
    ...customerSteps.map((s) => ({ kind: "step", step: s })),
    ...(hasBroker ? [{ kind: "handoff" }] : []),
    ...brokerSteps.map((s) => ({ kind: "step", step: s })),
  ];
  const firstBrokerFlowIdx = customerSteps.length + (hasBroker ? 1 : 0);

  // A broker resuming a handed-off claim starts in the broker phase, with the
  // customer phase shown as already done.
  const brokerPhaseActive = BROKER_STATUSES.includes(claimStatus);
  const initialIdx =
    isBroker && brokerPhaseActive && hasBroker ? firstBrokerFlowIdx : 0;

  const [activeIndex, setActiveIndex] = useState(initialIdx);
  const [maxIndex, setMaxIndex] = useState(initialIdx);
  const bodyRef = useRef(null);

  const lastIndex = Math.max(0, flow.length - 1);
  const safeActive = Math.min(activeIndex, lastIndex);
  const current = flow[safeActive];

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [safeActive]);

  const brokerView =
    current?.kind === "handoff" ||
    (current?.kind === "step" && current.step.phase === "broker");

  // While the broker completes a handed-off claim, the customer's steps are shown
  // read-only (they are frozen server-side too).
  const readOnlyStep =
    isBroker &&
    brokerPhaseActive &&
    current?.kind === "step" &&
    current.step.phase === "customer";

  // Field names owned by the customer phase — frozen at handoff. Named fields
  // contribute their own name; custom controls that manage form values declare
  // them via `lockNames` (e.g. the location control).
  const customerLockedFields = customerSteps
    .flatMap((s) =>
      (s.children || []).flatMap((c) => (c.name ? [c.name] : c.lockNames || []))
    )
    .filter(Boolean);

  const consentSatisfied = (step) =>
    !step?.requireCheck || Boolean(values?.[step.requireCheck]);

  // Per-step validation: a step is complete when none of its (visible) fields
  // carry a validation error. `errors` is computed eagerly by the FormRenderer
  // and already excludes hidden fields, so this gates "Weiter"/handoff/send on
  // the required fields the user can actually see.
  const isEmptyVal = (v) =>
    v === undefined ||
    v === null ||
    v === "" ||
    (Array.isArray(v) && v.length === 0);
  const errorsForStep = (step) =>
    (step?.children || []).filter((c) => c.name && errors?.[c.name]).length;
  // Fields a custom control manages (no named descriptor of their own) but that
  // are still required — declared per step via `requiredNames`.
  const requiredMissing = (step) =>
    (step?.requiredNames || []).some((n) => isEmptyVal(values?.[n]));
  const stepValid = (step) =>
    errorsForStep(step) === 0 && !requiredMissing(step);
  const allValid = (list) => list.every(stepValid);

  // "An Versicherer senden" is gated on EVERY step being valid, including ones
  // the broker may never have opened — so a dead button gave no clue what was
  // missing. Collect the offending steps and the field labels behind them.
  const missingInStep = (step) => [
    ...(step?.children || [])
      .filter((c) => c.name && errors?.[c.name])
      .map((c) => c.label || c.name),
    ...(step?.requiredNames || [])
      .filter((n) => isEmptyVal(values?.[n]))
      .map((n) => step?.requiredLabels?.[n] || n),
  ];
  const incompleteSteps = steps
    .map((step, index) => ({ step, index, missing: missingInStep(step) }))
    .filter((s) => !stepValid(s.step));

  const goTo = (idx) => {
    if (idx < 0 || idx > lastIndex || idx > maxIndex || isSubFormOpen) return;
    setActiveIndex(idx);
  };
  const advance = () => {
    const next = Math.min(lastIndex, safeActive + 1);
    setActiveIndex(next);
    setMaxIndex((m) => Math.max(m, next));
  };
  const back = () => {
    if (safeActive === 0) {
      actionContext?.handleCancel?.();
      return;
    }
    setActiveIndex(safeActive - 1);
  };

  const { submitAction, handleOnClick, handoffCallback, brokerSubmitCallback } =
    actionContext || {};

  const runAction = (e, callback, status) => {
    if (!callback || !handleOnClick) return;
    handleOnClick(e, {
      method: "callback",
      callback,
      additionalData: [
        { key: "status", value: status },
        { key: "customer_locked_fields", value: customerLockedFields },
      ],
    });
  };

  if (!current) return <Box />;

  const tenantName =
    activeBrand.labels?.tenantName || activeBrand.name || "Versicherungsmakler";
  const logo = activeBrand.assets?.logo;

  // Flow index of each step, for rail state.
  const flowIndexOf = (step) => flow.findIndex((f) => f.step === step);
  const railStatus = (fi) =>
    fi < maxIndex ? "done" : fi === safeActive ? "current" : "todo";

  const renderRailGroup = (title, groupSteps) => (
    <Box sx={{ mb: "20px" }}>
      <Typography
        sx={{
          fontSize: "10.5px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.09em",
          color: MUTED,
          mb: "12px",
          ml: "4px",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ position: "relative" }}>
        {/* connecting line behind the dots */}
        <Box
          sx={{
            position: "absolute",
            left: "13px",
            top: "12px",
            bottom: "12px",
            width: "2px",
            backgroundColor: RAIL_LINE,
          }}
        />
        {groupSteps.map((step) => {
          const fi = flowIndexOf(step);
          const st = railStatus(fi);
          const done = st === "done";
          const cur = st === "current";
          const reachable = fi <= maxIndex && !isSubFormOpen;
          return (
            <Box
              key={step.key}
              onClick={() => reachable && goTo(fi)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "11px",
                py: "5px",
                position: "relative",
                cursor: reachable ? "pointer" : "default",
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flex: "none",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: 700,
                  ...(done || cur
                    ? { backgroundColor: ACCENT, color: "#fff" }
                    : {
                        backgroundColor: "#fff",
                        color: "#aab4c2",
                        border: `2px solid ${RAIL_LINE}`,
                      }),
                  ...(cur ? { boxShadow: `0 0 0 4px rgba(74,120,188,0.18)` } : {}),
                }}
              >
                {done ? (
                  <CheckIcon sx={{ fontSize: 15 }} />
                ) : (
                  groupSteps.indexOf(step) + 1
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: cur ? 700 : 500,
                  color: cur ? NAVY : done ? "#374151" : "#aab4c2",
                }}
              >
                {step.railLabel || step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  const isLastCustomer =
    current?.kind === "step" &&
    current.step.phase !== "broker" &&
    flow[safeActive + 1]?.kind === "handoff";
  const isLastBroker =
    current?.kind === "step" && safeActive === lastIndex && brokerView;
  const isHandoff = current?.kind === "handoff";

  // Steps whose missing fields are currently blocking the forward button: every
  // step for the final "An Versicherer senden", just this one for "Weiter".
  const blockingSteps = isLastBroker
    ? incompleteSteps
    : current?.kind === "step" && !stepValid(current.step)
      ? [
          {
            step: current.step,
            index: safeActive,
            missing: missingInStep(current.step),
          },
        ]
      : [];

  const stepPill = isHandoff
    ? "Übergabe"
    : brokerView
      ? `Schritt ${brokerSteps.indexOf(current.step) + 1} von ${brokerSteps.length}`
      : `Schritt ${customerSteps.indexOf(current.step) + 1} von ${customerSteps.length}`;

  return (
    <Box
      sx={{
        backgroundColor: PAGE_BG,
        overflow: "hidden",
      }}
    >
      {/* Phase banner */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "13px",
          px: "32px",
          py: "11px",
          backgroundColor: brokerView ? NAVY : "#fff",
          borderBottom: brokerView ? "none" : "1px solid #eef0f3",
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: brokerView ? "rgba(255,255,255,0.12)" : CHIP_BG,
            color: brokerView ? "#fff" : ACCENT,
            fontSize: "11.5px",
            fontWeight: 700,
          }}
        >
          {brokerView ? (
            (tenantName[0] || "M").toUpperCase()
          ) : (
            <ShieldIcon sx={{ fontSize: 17 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "13.5px",
              fontWeight: 700,
              letterSpacing: "-0.1px",
              color: brokerView ? "#fff" : TITLE,
            }}
          >
            {brokerView ? `Bearbeitung durch ${tenantName}` : "Ihre Schadenmeldung"}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              mt: "1px",
              color: brokerView ? "rgba(255,255,255,0.7)" : MUTED,
            }}
          >
            {brokerView
              ? "Ergänzt die versicherungstechnischen Angaben."
              : "Wir führen Sie durch alle nötigen Angaben."}
          </Typography>
        </Box>
        <Box
          sx={{
            fontSize: "11.5px",
            fontWeight: 700,
            borderRadius: "20px",
            padding: "5px 12px",
            flex: "none",
            color: brokerView ? "#cfe0f5" : ACCENT,
            backgroundColor: brokerView ? "rgba(255,255,255,0.12)" : CHIP_BG,
          }}
        >
          {stepPill}
        </Box>
      </Box>

      {/* Body: rail + card column */}
      <Box sx={{ display: "flex", flex: 1, minHeight: "60vh" }}>
        {/* Left rail */}
        <Box
          className="scl"
          sx={{
            width: 212,
            flex: "none",
            padding: "26px 18px 26px 28px",
            borderRight: `1px solid ${RAIL_BORDER}`,
            overflowY: "auto",
          }}
        >
          {renderRailGroup("Ihre Angaben", customerSteps)}
          {hasBroker && renderRailGroup("Bearbeitung Makler", brokerSteps)}
        </Box>

        {/* Card column */}
        <Box
          ref={bodyRef}
          sx={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "30px 36px 40px" }}
        >
          {isHandoff ? (
            <HandoffInterstitial
              tenantName={tenantName}
              customerName={[
                values?.ansprechpartner_vorname,
                values?.ansprechpartner_name,
              ]
                .filter(Boolean)
                .join(" ")}
              onBack={back}
              onSubmit={(e) =>
                runAction(e, handoffCallback, "awaiting_broker_completion")
              }
              submitDisabled={isSubFormOpen || !allValid(customerSteps)}
            />
          ) : (
            <Box
              sx={{
                backgroundColor: "#fff",
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: "16px",
                boxShadow: "0 1px 3px rgba(16,40,80,0.05)",
                maxWidth: 960,
                width: "100%",
                mx: "auto",
                overflow: "hidden",
              }}
            >
              {/* card header */}
              <Box sx={{ padding: "30px 36px 8px" }}>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "7px",
                    mb: "14px",
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "6px",
                      border: "1px solid #e3e9f1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: "2px",
                      boxSizing: "border-box",
                      backgroundColor: "#fff",
                    }}
                  >
                    {logo && (
                      <Box
                        component="img"
                        src={logo}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: "12px", fontWeight: 600, color: MUTED }}
                  >
                    {brokerView ? "Maklerbearbeitung" : "Schaden-Assistent"}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "22px",
                    fontWeight: 700,
                    color: TITLE,
                    letterSpacing: "-0.3px",
                    mb: current?.step?.description ? "7px" : 0,
                  }}
                >
                  {current?.step?.label}
                </Typography>
                {current?.step?.description && (
                  <Typography
                    sx={{ fontSize: "14px", color: INTRO, lineHeight: 1.6 }}
                  >
                    {current.step.description}
                  </Typography>
                )}
                {readOnlyStep && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: MUTED,
                      fontStyle: "italic",
                      mt: "12px",
                    }}
                  >
                    Vom Kunden erfasst – kann in der Maklerbearbeitung nicht mehr
                    geändert werden.
                  </Typography>
                )}
              </Box>
              {/* card body: fields — soft input styling to match the dummy */}
              <Box
                sx={{
                  padding: "22px 36px 30px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#f8fafc",
                    borderRadius: "9px",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                      borderWidth: "1.5px",
                    },
                  },
                  "& .MuiFilledInput-root": {
                    backgroundColor: "#f8fafc",
                    borderRadius: "9px",
                  },
                }}
              >
                <Grid container spacing={2}>
                  {renderFields(current.step.children, readOnlyStep)}
                </Grid>
              </Box>
            </Box>
          )}

          {/* Why the forward button is disabled: name the missing fields. Both
              buttons were dead-ends before — "Weiter" gates on the current step,
              "An Versicherer senden" on every step, neither said which field. */}
          {!isHandoff && blockingSteps.length > 0 && (
            <Box
              sx={{
                maxWidth: 960,
                width: "100%",
                mx: "auto",
                mt: "20px",
                padding: "12px 16px",
                borderRadius: "10px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
              }}
            >
              <Typography
                sx={{ fontSize: "13px", fontWeight: 600, color: "#b91c1c" }}
              >
                {isLastBroker
                  ? "Vor dem Senden fehlen noch Angaben:"
                  : "Es fehlen noch Angaben:"}
              </Typography>
              {blockingSteps.map(({ step, index, missing }) => (
                <Typography
                  key={step.key ?? index}
                  onClick={() => goTo(index)}
                  sx={{
                    fontSize: "13px",
                    color: "#b91c1c",
                    mt: "4px",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textDecorationStyle: "dotted",
                  }}
                >
                  {step.railLabel || step.label}
                  {missing.length > 0 && `: ${missing.join(", ")}`}
                </Typography>
              ))}
            </Box>
          )}

          {/* Footer (below the card) — the handoff card carries its own buttons */}
          {!isHandoff && (
          <Box
            sx={{
              maxWidth: 960,
              width: "100%",
              mx: "auto",
              mt: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="tertiary"
              startIcon={<BackIcon />}
              disabled={isSubFormOpen}
              onClick={back}
              sx={{ borderColor: HAIRLINE, color: INTRO }}
            >
              {safeActive === 0 ? "Abbrechen" : "Zurück"}
            </Button>

            <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
              {submitAction && (
                <Button
                  variant="text"
                  disabled={isSubFormOpen}
                  onClick={(e) => handleOnClick?.(e, submitAction)}
                  sx={{ color: MUTED }}
                >
                  Entwurf speichern
                </Button>
              )}
              {isLastBroker ? (
                <Button
                  variant="contained"
                  endIcon={<NextIcon />}
                  disabled={isSubFormOpen || !allValid(steps)}
                  onClick={(e) => runAction(e, brokerSubmitCallback, "processing")}
                >
                  An Versicherer senden
                </Button>
              ) : (
                <Button
                  variant="contained"
                  endIcon={<NextIcon />}
                  disabled={
                    isSubFormOpen ||
                    !stepValid(current.step) ||
                    (isLastCustomer && !consentSatisfied(current.step))
                  }
                  onClick={advance}
                >
                  Weiter
                </Button>
              )}
            </Box>
          </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function HandoffInterstitial({
  tenantName,
  customerName,
  onBack,
  onSubmit,
  submitDisabled,
}) {
  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(16,40,80,0.06)",
        maxWidth: 480,
        mx: "auto",
        textAlign: "center",
        padding: "34px 38px",
      }}
    >
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: "50%",
          mx: "auto",
          mb: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: CHIP_BG,
          color: ACCENT,
        }}
      >
        <CheckIcon sx={{ fontSize: 30 }} />
      </Box>
      <Typography
        sx={{
          fontSize: "20px",
          fontWeight: 700,
          color: TITLE,
          letterSpacing: "-0.3px",
          mb: "10px",
        }}
      >
        {customerName ? `Vielen Dank, ${customerName}!` : "Vielen Dank für Ihre Angaben!"}
      </Typography>
      <Typography sx={{ fontSize: "14px", color: INTRO, lineHeight: 1.6, mb: "22px" }}>
        Ihre Angaben sind vollständig und gehen jetzt an Ihren persönlichen
        Ansprechpartner bei {tenantName}. Er ergänzt die versicherungstechnischen
        Angaben und meldet sich bei Rückfragen.
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Button
          variant="tertiary"
          startIcon={<BackIcon />}
          onClick={onBack}
          sx={{ borderColor: HAIRLINE, color: INTRO }}
        >
          Zurück
        </Button>
        <Button
          variant="contained"
          endIcon={<NextIcon />}
          disabled={submitDisabled}
          onClick={onSubmit}
        >
          Weiter zur Maklerbearbeitung
        </Button>
      </Box>
    </Box>
  );
}

HandoffInterstitial.propTypes = {
  tenantName: PropTypes.string,
  customerName: PropTypes.string,
  onBack: PropTypes.func,
  onSubmit: PropTypes.func,
  submitDisabled: PropTypes.bool,
};

PhaseRail.propTypes = {
  item: PropTypes.object.isRequired,
  errors: PropTypes.object,
  renderFields: PropTypes.func.isRequired,
  actionContext: PropTypes.object,
  values: PropTypes.object,
  isSubFormOpen: PropTypes.bool,
  claimStatus: PropTypes.string,
  isBroker: PropTypes.bool,
};
